import * as React from 'react'
import { useState } from 'react'
import { GetStaticProps } from 'next'
import { NextSeo } from 'next-seo'
import { Formik, Form, useField } from 'formik'
import Image from 'next/image'
import Link from 'next/link'
import Container from '~/components/Container'
import NotchedCard from '~/components/NotchedCard'
import SupportIcon from '~/svg/contact-support.svg'
import GrowersIcon from '~/svg/contact-growers.svg'
import InvestorsIcon from '~/svg/contact-investors.svg'
import RightArrowIcon from '~/svg/contact-right-arrow.svg'
import contactSchema from '~/schema/contact-schema.js'
import ContactFormSubmittedIcon from '~/svg/contact-form-submitted-icon.svg'
import ContactFormSubmittedErrorIcon from '~/svg/contact-form-submitted-error-icon.svg'

const title = 'Contact Us - Mary AG'
const description = ''

export const getStaticProps: GetStaticProps = async () => {
  const data = {
    options: ['Product Questions', 'Technical Support']
  }
  return { props: { data } }
}

const TextInput = ({
  label,
  filledAndValid,
  lg,
  ...props
}: {
  [x: string]: any
  name: string
  filledAndValid: boolean
  lg?: boolean
}) => {
  const [field, meta, helpers] = useField(props)

  return (
    <div className="pb-6 lg:pb-0 flex flex-col">
      <div className="">
        <div className="inline-flex">
          <a
            className={`text-sm ${
              filledAndValid ? 'text-gray-300' : 'text-white'
            } text-sans-serif pb-2`}
          >
            {props.title}
          </a>
          {meta.touched && meta.error ? (
            <div className="error text-red-500 text-sm p-0 pl-4">
              {meta.error}
            </div>
          ) : null}
        </div>

        <div
          className={`w-full bg-grey-600 rounded-md ${
            lg ? `h-48 lg:h-80` : 'h-auto'
          }`}
        >
          {lg ? (
            <textarea
              required
              autoComplete="off"
              className={`h-44 mt-2 text-input ml-1 pr-10 text-white border-none focus:ring-0 text-sm placeholder-grey-300 w-full rounded-md bg-transparent`}
              style={{ resize: 'none' }}
              {...field}
              {...props}
            />
          ) : (
            <input
              required
              autoComplete="off"
              className={`h-14 ml-1 pr-10 text-input text-white border-none focus:ring-0 text-sm placeholder-grey-300 w-full rounded-md bg-transparent `}
              {...field}
              {...props}
            />
          )}
        </div>
      </div>
    </div>
  )
}
const SelectInput = ({
  label,
  filledAndValid,
  ...props
}: {
  [x: string]: any
  name: string
  filledAndValid: boolean
}) => {
  const [field, meta] = useField(props)

  return (
    <div className="h-full">
      <div className="inline-flex">
        <a
          className={`text-sm ${
            filledAndValid ? 'text-gray-300' : 'text-white'
          } text-sans-serif pb-2`}
        >
          {props.title}
        </a>
        {meta.touched && meta.error ? (
          <div className="error text-red-500 text-sm p-0 pl-4">
            {meta.error}
          </div>
        ) : null}
      </div>
      <div className=" w-full">
        <div className="flex flex-row justify-between items-center p-0 w-full">
          <style jsx>{`
            select:invalid {
              color: #696969;
              hidden
            }
          `}</style>
          <select
            id="dropdown"
            required
            {...field}
            {...props}
            className="bg-grey-600 rounded-md border-none focus:ring-0 text-sm w-full h-14 pl-4 "
          />
        </div>
      </div>
    </div>
  )
}

type formState = 'preSubmit' | 'postSubmit' | 'success' | 'error'

export default function ContactUsPage(props) {
  const [submitted, setSubmitted] = useState<formState>('preSubmit')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')

  const options = props.data.options

  const resetForm = () => {
    setFirstName('')
    setLastName('')
    setEmail('')
    setPhone('')
    setSubject('')
    setMessage('')
  }

  const saveForm = (values) => {
    setFirstName(values.firstName)
    setLastName(values.lastName)
    setEmail(values.email)
    setPhone(values.phone)
    setSubject(values.subject)
    setMessage(values.message)
  }

  const ContactForm = ({ options }) => (
    <Formik
      initialValues={{
        firstName: firstName,
        lastName: lastName,
        email: email,
        phone: phone,
        subject: subject,
        message: message
      }}
      validationSchema={contactSchema}
      onSubmit={(values, actions) => {
        saveForm(values)
        contactFormSubmit(values)
        actions.setSubmitting(false)
      }}
    >
      {({ isValid, dirty }) => (
        <Form
          hidden={submitted !== 'preSubmit'}
          className="flex flex-col pb-28"
        >
          <div className="lg:grid grid-rows-4 grid-cols-2 grid-flow-col gap-x-4">
            <div className="row-span-1 flex flex-col justify-end">
              <TextInput
                label="firstName"
                name="firstName"
                type="text"
                placeholder="Enter First Name"
                title="First Name"
                filledAndValid={isValid && dirty}
              />
            </div>
            <div className="row-span-1 flex flex-col justify-end lg:pt-8">
              <TextInput
                label="lastName"
                name="lastName"
                type="text"
                placeholder="Enter Last Name"
                title="Last Name"
                filledAndValid={isValid && dirty}
              />
            </div>
            <div className="row-span-1 flex flex-col justify-end lg:pt-8">
              <TextInput
                label="email"
                name="email"
                type="text"
                placeholder="Email Address"
                title="Email Address"
                filledAndValid={isValid && dirty}
              />
            </div>
            <div className="row-span-1 flex flex-col justify-end lg:pt-8">
              <TextInput
                label="phone"
                name="phone"
                type="text"
                placeholder="123-456-7890"
                title="Phone Number"
                filledAndValid={isValid && dirty}
              />
            </div>

            <div className="row-span-1 flex flex-col justify-end lg:pt-8 text-white">
              <SelectInput
                label="Subject"
                name="subject"
                title="How Can We Help You?"
                filledAndValid={isValid && dirty}
              >
                <option value="" selected disabled hidden>
                  Please Select
                </option>

                {options.map((option) => {
                  return (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  )
                })}
              </SelectInput>
            </div>
            <div className="row-span-3 flex flex-col pt-6 lg:pt-8 h-full">
              <TextInput
                label="message"
                name="message"
                type="text"
                placeholder="Enter Your Message"
                title="Your Message"
                lg
                filledAndValid={isValid && dirty}
              />
            </div>
          </div>
          {!(isValid && dirty) || submitted === 'postSubmit' ? (
            <button
              className="rounded-full py-3 px-12 mt-8 h-16 text-white focus:outline-none justify-end self-end items-end bg-grey-600"
              type="submit"
              disabled
            >
              <span className="text-sm text-darkgray-500 text-white">
                Submit Message
              </span>
            </button>
          ) : (
            <button
              className="rounded-full py-3 px-12 mt-8 h-16 text-grey-700 focus:outline-none justify-end self-end items-end bg-pink-500"
              type="submit"
            >
              <span className="text-sm text-darkgray-500 text-white">
                Submit Message
              </span>
            </button>
          )}
        </Form>
      )}
    </Formik>
  )

  const SubmittedFormSuccess = () => (
    <div className="flex flex-col justify-center items-center pt-24">
      <ContactFormSubmittedIcon />
      <p className="lg:w-1/4 w-4/5 text-center pt-12 pb-12 text-white text-xl">
        Thank you for your message! <br /> We will be in touch with you shortly.
      </p>
      <button
        className="self-center lg:self-end bg-grey-600 rounded-full py-2 px-6 mb-20 focus:outline-none text-white text-base"
        onClick={() => setSubmitted('preSubmit')}
      >
        Send Another Message
      </button>
    </div>
  )

  const SubmittedFormError = () => (
    <div className="flex flex-col justify-center items-center pt-24">
      <ContactFormSubmittedErrorIcon />
      <p className="lg:w-1/4 w-4/5 text-center pt-12 pb-12 text-white text-xl">
        Oh No... An unexpected error occured and we couldn&apos;t Receive your
        message. Please Try Again!
      </p>
      <button
        className="self-center lg:self-end bg-grey-600 rounded-full py-2 px-6 mb-20 focus:outline-none text-white text-base"
        onClick={() => setSubmitted('preSubmit')}
      >
        Send Another Message
      </button>
    </div>
  )

  const contactFormSubmit = async (data) => {
    setSubmitted('postSubmit')

    const res = await fetch('/api/contact', {
      body: JSON.stringify({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        subject: data.subject,
        message: data.message
      }),
      headers: {
        'Content-Type': 'application/json'
      },
      method: 'POST'
    })

    if (res.status === 200) {
      setSubmitted('success')
      resetForm()
    } else {
      console.log(res)
      setSubmitted('error')
    }
  }

  return (
    <Container className="px-8 lg:px-28 overflow-x-hidden">
      <NextSeo title={title} description={description} />

      <h2 className="text-white font-medium pt-28 text-2xl lg:text-4xl lg:text-center pb-12">
        Contact Us
      </h2>
      {/* <div className="lg:-mx-0 flex flex-col">
        <div
          id="contact-cards"
          className="-mx-8 lg:mx-auto lg:max-w-7xl flex flex-row flex-nowrap overflow-x-scroll lg:flex-wrap pb-24 lg:overflow-x-visible"
        >
          <div className="pl-8 lg:hidden "></div>
          <NotchedCard
            notchPosition="top-right"
            className="bg-grey-600 flex flex-row flex-grow-0 flex-shrink-0 sm:w-96 w-80 mt-4 rounded-lg text-white"
          >
            <div className="flex flex-col px-6">
              <div>
                <SupportIcon className="mt-12" />
                <p className="text-lg text-white mt-8 font-sans-serif font-regular">
                  Visit our support page for content straight from our amazing
                  community
                </p>
              </div>
              <div className="flex flex-row items-center pb-10 pt-14">
                <Link href="/support">
                  <a className="pr-2 text-sm font-sans-serif font-regular text-white">
                    Support
                  </a>
                </Link>
                <RightArrowIcon />
              </div>
            </div>
          </NotchedCard>
          <NotchedCard
            notchPosition="top-right"
            className="bg-grey-600 flex flex-row flex-grow-0 flex-shrink-0 sm:w-96 w-80 mx-4 mt-4 rounded-lg text-white"
          >
            <div className="flex flex-col  px-6">
              <div>
                <GrowersIcon className="mt-12" />
                <p className="text-lg text-white mt-8 font-sans-serif font-regular">
                  Visit our growers page for content straight from our amazing
                  community
                </p>
              </div>
              <div className="flex flex-row items-center pb-10 pt-14">
                <Link href="/growers">
                  <a className="pr-2 text-sm font-sans-serif font-regular text-white">
                    Growers
                  </a>
                </Link>
                <RightArrowIcon />
              </div>
            </div>
          </NotchedCard>
          <NotchedCard
            notchPosition="top-right"
            className="bg-grey-600 flex flex-row pb-8 flex-grow-0 flex-shrink-0 sm:w-96 w-80 mt-4 rounded-lg text-white"
          >
            <div className="flex flex-col  px-6">
              <div>
                <InvestorsIcon className="mt-12" />
                <p className="text-lg text-white mt-8 font-sans-serif font-regular">
                  Visit our investors page for annual reports and company news
                </p>
              </div>
              <div className="flex flex-row items-center pb-10 pt-14">
                <Link href="/investors">
                  <a className="pr-2 text-sm font-sans-serif font-regular text-white">
                    Investors
                  </a>
                </Link>
                <RightArrowIcon />
              </div>
            </div>
          </NotchedCard>
          <div className="pr-8 lg:hidden"></div>
        </div>
        <style jsx>{`
          #contact-cards {
            scrollbar-width: none;
            scrollbar-color: #030303;
          }

          #contact-cards::-webkit-scrollbar {
            display: none;
            width: 0px;
          }

          #contact-cards::-webkit-scrollbar-track {
            background: #030303;
          }

          #contact-cards::-webkit-scrollbar-thumb {
            background: #030303;
          }
        `}</style>
      </div> */}
      <div className="lg:mx-auto w-full lg:max-w-7xl">
        <h2 className="text-xl lg:text-2xl text-left pb-12 text-white">
          Send Us A Message
        </h2>
        {submitted === 'preSubmit' || submitted === 'postSubmit' ? (
          <ContactForm options={options} />
        ) : submitted === 'success' ? (
          <SubmittedFormSuccess />
        ) : (
          <SubmittedFormError />
        )}
      </div>
      <div className="hidden lg:block -mx-8 lg:-mx-28">
        <Image
          alt="contact-page-image"
          src="/images/contact-page-image.png"
          width="100%"
          height="50%"
          layout="responsive"
          objectFit="fill"
          className="aspect-w-17 aspect-h-10"
        />
      </div>
      <div className="lg:hidden -mx-8 lg:-mx-28">
        <Image
          alt="contact-page-image"
          src="/images/contact-page-image-mobile.png"
          width="130%"
          height="100%"
          layout="responsive"
          objectFit="fill"
          className="aspect-w-13 aspect-h-10"
        />
      </div>
    </Container>
  )
}
